import React, { useEffect, useRef } from 'react';
import { useIsFinePointer } from '../hooks/useIsFinePointer';
import './Cursor.scss';

// Custom cursor: position is driven directly off a single mousemove listener
// (no rAF loop), and hover state is toggled via mouseover/mouseout delegation
// against elements marked with `data-cursor-hover`. All visual reactions
// (halo scale/opacity, glyph color) are pure CSS transitions.
const Cursor: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const isFinePointer = useIsFinePointer();

  useEffect(() => {
    const root = rootRef.current;
    document.documentElement.classList.remove('has-fine-pointer');
    if (!root || !isFinePointer) return;

    const handleMove = (e: MouseEvent) => {
      root.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;

      if (!root.classList.contains('is-ready')) {
        // Apply the first real coordinates while hidden and without a
        // transition. The cursor can then be revealed in place instead of
        // animating in from its off-screen fallback after a page refresh.
        void root.offsetWidth;
        root.classList.add('is-ready');
        document.documentElement.classList.add('has-fine-pointer');
      }
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest('[data-cursor-hover]')) {
        root.classList.add('is-hovering');
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const hoverEl = target?.closest('[data-cursor-hover]');
      if (!hoverEl) return;
      const related = e.relatedTarget as Node | null;
      if (!related || !hoverEl.contains(related)) {
        root.classList.remove('is-hovering');
      }
    };

    document.addEventListener('mousemove', handleMove, { passive: true });
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.documentElement.classList.remove('has-fine-pointer');
    };
  }, [isFinePointer]);

  // Skip rendering entirely on touch devices instead of relying on CSS
  // media-query gating alone (some mobile browsers misreport `hover: hover`).
  if (!isFinePointer) return null;

  return (
    <div className="cursor" ref={rootRef} aria-hidden="true">
      <span className="cursor__halo" />
      <span className="cursor__glyph-anchor">
        <span className="cursor__glyph">➤</span>
      </span>
    </div>
  );
};

export default Cursor;
